import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';

import { PlannerPage } from './PlannerPage';
import { TripPage } from './TripPage';

/**
 * One entry in the menu, two kinds of journey behind it.
 *
 * A day out and a week away are the same act of planning at different
 * lengths, and two menu items made a traveller choose before they had been
 * told what the difference was. Asked here instead, in one line, above the
 * form it selects.
 *
 * The choice lives in the URL rather than in state, so a half-filled form
 * survives a reload and both kinds are linkable — and because the two pages
 * hold entirely separate state, which a shared parent would have to keep
 * alive and synchronised for no benefit.
 */

type Kind = 'day' | 'trip';

export function PlanChooserPage() {
  const { kind } = useParams<{ kind?: string }>();
  const navigate = useNavigate();
  const current: Kind = kind === 'trip' ? 'trip' : 'day';

  /*
   * A flex column filling the height the shell gives it.
   *
   * Both planners size themselves at `height: 100%` and scroll their own
   * sidebar, so a plain wrapper resolved that against nothing: the sidebar's
   * scrollbar disappeared and the whole page grew instead.
   */
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Paper
        square
        elevation={0}
        sx={{
          px: 2, py: 0.75,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            data-tour="plan-kind"
            exclusive
            size="small"
            value={current}
            /*
             * `replace`, so switching kind does not stack history entries a
             * traveller then has to press Back through to leave the planner.
             */
            onChange={(_, next: Kind | null) =>
              next && navigate(`/plan/${next}`, { replace: true })}
          >
            <ToggleButton value="day" sx={{ px: 2, gap: 0.75 }}>
              <TodayOutlinedIcon fontSize="small" />
              Single day
            </ToggleButton>
            <ToggleButton value="trip" sx={{ px: 2, gap: 0.75 }}>
              <DateRangeOutlinedIcon fontSize="small" />
              Multi-day operation
            </ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="caption" color="text.secondary">
            {current === 'day'
              ? 'One tasking, departure to return.'
              : 'A day at a time, with no site covered twice.'}
          </Typography>
        </Stack>
      </Paper>

      {/*
        Mounted one at a time on purpose. Keeping both alive would hold two
        maps, two POI layers and two sets of place lookups for a screen nobody
        is looking at — and switching kind is a deliberate act, not a tab.
      */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {current === 'day' ? <PlannerPage /> : <TripPage />}
      </Box>
    </Box>
  );
}
